import { ProductCategory } from "@utils/constants";
import { ProductCategoryStyle } from "./types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { DefaultTheme } from "styled-components/native";

const getHeaderStyleBasedOnProduct = (
  category: ProductCategory,
  theme?: DefaultTheme
): ProductCategoryStyle => {
  const productTheme = getProductThemeFromType(category);
  if (category === ProductCategory.DEFAULT) {
    return {
      textColor: "header-cont",
      background: theme?.isLargeScreen ? "core-01" : "header-bg-static",
    };
  }

  return {
    textColor: `${productTheme}-05`,
    background: `${productTheme}-01`,
  };
};

export const getPriceModeColorCode = (priceMode: number) => {
  switch (priceMode) {
    case PriceModes.GREEN:
      return "success-01";
    case PriceModes.ORANGE:
      return "warn-01";
    default:
      return "core-04";
  }
};

export const getProductThemeFromType = (category: ProductCategory) => {
  let productTheme = "default";
  switch (category) {
    case ProductCategory.DIAMOND:
      productTheme = "diam";
      break;
    case ProductCategory.GEMSTONE:
      productTheme = "gem";
      break;
    case ProductCategory.JEWELRY:
    case ProductCategory.LABGROWN_JEWELRY:
      productTheme = "jewelry";
      break;
    case ProductCategory.LAB_GROWN_DIAMOND:
      productTheme = "lab";
      break;
    default:
      productTheme = "default";
      break;
  }
  return productTheme;
};

const hexToRgb = (hex: string) => {
  hex = hex ? hex?.replace("#", "") : "";

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return [r, g, b];
};

const isColorLight = (hex: string) => {
  const rgb = hexToRgb(hex);
  const luminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  return luminance > 128;
};

enum HeaderItem {
  PROFILE = "profile",
  LOGO = "logo",
  ADVERTISEMENT = "advertisement",
  SEARCH = "search",
  BULK_LIST = "bulk-list",
  LINK = "link",
  SHOPPING_CART = "shopping-cart",
  SEARCH_MARKETPLACE = "search-marketplace",
  USER_PROFILE = "user-profile",
  ORDER_CART = "order-cart",
}

type HeaderItemConfig = {
  name: string;
  icon?: string;
  iconPosition?: "left" | "center" | "right";
  label?: string;
  url?: string;
  link?: string;
  width?: string;
  color?: string;
  isInternal?: boolean;
};

type HeaderConfig = {
  backgroundColor: string;
  height: number;
  isNativeHeaderHidden?: boolean;
  htmlHeader?: string;
  hasDivider?: boolean;
  maxWidth?: number;
  left: { items: HeaderItemConfig[]; width: string; containerWidth: string };
  center: { items: HeaderItemConfig[]; width: string; containerWidth: string };
  right: { items: HeaderItemConfig[]; width: string; containerWidth: string };
};

const extractWidthValue = (
  width: string | number | undefined
): number | null => {
  if (width === undefined) {
    return null;
  }

  if (typeof width === "number") {
    return width;
  }

  const numericPart = width.match(/\d+(\.\d+)?/);
  return numericPart ? parseFloat(numericPart[0]) : null;
};

export const deepCompareObjects = (obj1: any, obj2: any) => {
  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }

  obj1 = convertValuesToString(obj1);
  obj2 = convertValuesToString(obj2);

  for (let key of keys1) {
    if (!keys2.includes(key) || obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

const convertValuesToString = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, String(value)])
  );
};

export {
  getHeaderStyleBasedOnProduct,
  isColorLight,
  HeaderItem,
  HeaderItemConfig,
  HeaderConfig,
  extractWidthValue,
};
